/**
 * The create-edit dialog of the external-calendar sources section (Settings → Calendar,
 * CR-31/33). Create mode offers the kind choice (caldav / ews / ad / ics), the kind's config
 * fields, optional credentials and the sync interval; a draft "Check connection" probe
 * (POST /sources/check) validates the field values against the real server before anything
 * is saved - a successful CalDAV probe lists the discovered collections and a click fills
 * the collectionPath. Edit mode locks the kind (the protocol never changes), drops the draft
 * probe (stored credentials are not in the form - the row-level Check covers the saved state
 * honestly) and PATCHes only the editable fields; blank credentials keep the sealed ones.
 */

import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  checkSource,
  createSource,
  patchSource,
  type CalendarSource,
  type DistributiveOmit,
  type SourceCheck,
  type SourceConfig,
  type SourceCredentials,
} from './api';
import { showToast } from './Toast';

export type SourceKind = CalendarSource['kind'];

const KIND_CHOICES: readonly { readonly kind: SourceKind; readonly labelKey: string }[] = [
  { kind: 'caldav', labelKey: 'calendar.sources.kind_caldav' },
  { kind: 'ews', labelKey: 'calendar.sources.kind_ews' },
  { kind: 'ad', labelKey: 'calendar.sources.kind_ad' },
  { kind: 'ics', labelKey: 'calendar.sources.kind_ics' },
];

/** The server bounds the interval at one month (0 = manual only); these are the presets. */
const INTERVAL_PRESETS: readonly number[] = [0, 900, 3600, 21600, 86400];
const MAX_INTERVAL_SECS = 30 * 24 * 3600;

/** Human label of a sync interval: 0 manual, whole hours as h, anything else as minutes. */
export function intervalLabel(
  translate: (key: string, options?: Record<string, unknown>) => string,
  secs: number,
): string {
  if (secs === 0) {
    return translate('calendar.sources.interval_manual');
  }
  if (secs % 3600 === 0) {
    return translate('calendar.sources.interval_hours', { count: secs / 3600 });
  }
  return translate('calendar.sources.interval_minutes', { count: Math.round(secs / 60) });
}

/** The interval choices offered by the select; a stored non-preset value joins as a custom entry. */
function intervalOptions(storedSecs: number | null): readonly number[] {
  if (storedSecs === null || INTERVAL_PRESETS.includes(storedSecs)) {
    return INTERVAL_PRESETS;
  }
  return [...INTERVAL_PRESETS, storedSecs].sort((a, b) => a - b);
}

function parsePort(raw: string): number | null {
  const port = Number(raw);
  return Number.isInteger(port) && port >= 1 && port <= 65535 ? port : null;
}

/** The flat controlled draft of the form; only the chosen kind's config fields matter. */
interface SourceDraft {
  name: string;
  serverUrl: string;
  collectionPath: string;
  ewsUrl: string;
  mailbox: string;
  allowHttp: boolean;
  allowNtlm: boolean;
  adHost: string;
  adPort: string;
  adStartTls: boolean;
  adBaseDn: string;
  adFilter: string;
  icsUrl: string;
  username: string;
  password: string;
  intervalChoice: string;
}

const EMPTY_DRAFT: SourceDraft = {
  name: '',
  serverUrl: '',
  collectionPath: '',
  ewsUrl: '',
  mailbox: '',
  allowHttp: false,
  allowNtlm: false,
  adHost: '',
  adPort: '389',
  adStartTls: true,
  adBaseDn: '',
  adFilter: '',
  icsUrl: '',
  username: '',
  password: '',
  intervalChoice: '900',
};

function draftFromSource(source: CalendarSource): SourceDraft {
  const base: SourceDraft = {
    ...EMPTY_DRAFT,
    name: source.name,
    intervalChoice: `${source.syncIntervalSecs}`,
  };
  const config = source.config;
  if (config.kind === 'caldav') {
    return { ...base, serverUrl: config.serverUrl, collectionPath: config.collectionPath };
  }
  if (config.kind === 'ews') {
    return {
      ...base,
      ewsUrl: config.url,
      mailbox: config.mailbox,
      allowHttp: config.allowHttp,
      allowNtlm: config.allowNtlm,
    };
  }
  if (config.kind === 'ad') {
    return {
      ...base,
      adHost: config.host,
      adPort: `${config.port}`,
      adStartTls: config.startTls,
      adBaseDn: config.baseDn,
      adFilter: config.filter,
    };
  }
  return { ...base, icsUrl: config.url };
}

/** True when every config field the kind needs is filled (the port parses for ad). */
function configComplete(kind: SourceKind, draft: SourceDraft): boolean {
  switch (kind) {
    case 'caldav':
      return draft.serverUrl.trim().length > 0 && draft.collectionPath.trim().length > 0;
    case 'ews':
      return draft.ewsUrl.trim().length > 0 && draft.mailbox.trim().length > 0;
    case 'ad':
      return (
        draft.adHost.trim().length > 0 &&
        draft.adBaseDn.trim().length > 0 &&
        parsePort(draft.adPort) !== null
      );
    case 'ics':
      return draft.icsUrl.trim().length > 0;
  }
}

/** Completeness for the draft probe: a CalDAV collection path is NOT required there -
 * discovery probes "/" and its answer fills the real path. */
function configCompleteForCheck(kind: SourceKind, draft: SourceDraft): boolean {
  if (kind === 'caldav') {
    return draft.serverUrl.trim().length > 0;
  }
  return configComplete(kind, draft);
}

/** Builds the wire config for the chosen kind (no discriminator inside - the request's
 * `kind` field picks the shape and the backend parses the config strictly); call only when
 * {@link configComplete} holds. */
function draftToConfig(kind: SourceKind, draft: SourceDraft): DistributiveOmit<SourceConfig, 'kind'> {
  switch (kind) {
    case 'caldav':
      return {
        serverUrl: draft.serverUrl.trim(),
        collectionPath: draft.collectionPath.trim(),
      };
    case 'ews':
      return {
        url: draft.ewsUrl.trim(),
        mailbox: draft.mailbox.trim(),
        allowHttp: draft.allowHttp,
        allowNtlm: draft.allowNtlm,
      };
    case 'ad':
      return {
        host: draft.adHost.trim(),
        port: parsePort(draft.adPort) ?? 0,
        startTls: draft.adStartTls,
        baseDn: draft.adBaseDn.trim(),
        filter: draft.adFilter.trim(),
      };
    case 'ics':
      return { url: draft.icsUrl.trim() };
  }
}

/** Credentials travel only when both fields are filled; blank keeps the stored pair. */
function draftCredentials(draft: SourceDraft): SourceCredentials | undefined {
  if (draft.username.trim().length === 0 || draft.password.length === 0) {
    return undefined;
  }
  return { username: draft.username.trim(), password: draft.password };
}

/** Config for the draft probe: an empty CalDAV collection path probes "/" (discovery does
 * not need the real path, but the endpoint requires a structurally valid config). */
function checkConfig(kind: SourceKind, draft: SourceDraft): DistributiveOmit<SourceConfig, 'kind'> {
  if (kind === 'caldav') {
    const path = draft.collectionPath.trim();
    return {
      serverUrl: draft.serverUrl.trim(),
      collectionPath: path.length > 0 ? path : '/',
    };
  }
  return draftToConfig(kind, draft);
}

function draftIntervalSecs(draft: SourceDraft): number | null {
  const secs = Number(draft.intervalChoice);
  return Number.isInteger(secs) && secs >= 0 && secs <= MAX_INTERVAL_SECS ? secs : null;
}

/** Client-side validation; returns raw i18n error keys per field, empty when valid. */
function validateDraft(kind: SourceKind, draft: SourceDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  if (draft.name.trim().length === 0) {
    errors.name = 'calendar.sources.errors.name_required';
  }
  if (!configComplete(kind, draft)) {
    errors.config = 'calendar.sources.errors.config_incomplete';
  }
  if (draft.password.length > 0 && draft.username.trim().length === 0) {
    errors.username = 'calendar.sources.errors.username_required';
  }
  return errors;
}

/** Outcome of the draft probe; `kind` snapshots what was probed (the user can switch after). */
type DraftCheck =
  | { readonly status: 'checking' }
  | { readonly status: 'done'; readonly kind: SourceKind; readonly check: SourceCheck }
  | { readonly status: 'failed' };

export interface SourceFormProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Fresh bearer token from the SSO session; the dialog assumes it is valid. */
  readonly token: string;
  /** Non-null opens the dialog as the edit form of that source; null is the create form. */
  readonly source: CalendarSource | null;
  /** Called after a successful create or patch so the section refreshes the list. */
  readonly onSaved: (source: CalendarSource) => void;
}

export function SourceForm({
  open,
  onOpenChange,
  token,
  source,
  onSaved,
}: SourceFormProps): JSX.Element {
  const { t } = useTranslation();
  const editing = source !== null;
  const [kind, setKind] = useState<SourceKind>('caldav');
  const [draft, setDraft] = useState<SourceDraft>(EMPTY_DRAFT);
  const [fieldErrors, setFieldErrors] = useState<Readonly<Record<string, string>>>({});
  const [draftCheck, setDraftCheck] = useState<DraftCheck | null>(null);
  const [pending, setPending] = useState(false);
  const [checking, setChecking] = useState(false);

  // Re-arm the whole form whenever the dialog (re)opens for a target.
  useEffect(() => {
    if (!open) {
      return;
    }
    setKind(source?.kind ?? 'caldav');
    setDraft(source !== null ? draftFromSource(source) : EMPTY_DRAFT);
    setFieldErrors({});
    setDraftCheck(null);
    setPending(false);
    setChecking(false);
  }, [open, source]);

  function update(patch: Partial<SourceDraft>): void {
    setDraft((current) => ({ ...current, ...patch }));
    setDraftCheck(null);
    setFieldErrors({});
  }

  function chooseKind(next: SourceKind): void {
    setKind(next);
    setDraftCheck(null);
    setFieldErrors({});
  }

  async function handleCheck(): Promise<void> {
    if (checking) {
      return;
    }
    const errors: Record<string, string> = {};
    if (!configCompleteForCheck(kind, draft)) {
      errors.config = t('calendar.sources.errors.config_incomplete');
    }
    if (draft.password.length > 0 && draft.username.trim().length === 0) {
      errors.username = t('calendar.sources.errors.username_required');
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    const credentials = draftCredentials(draft);
    setChecking(true);
    try {
      const check = await checkSource(token, {
        kind,
        config: checkConfig(kind, draft),
        ...(credentials !== undefined ? { credentials } : {}),
      });
      setDraftCheck({ status: 'done', kind, check });
    } catch {
      setDraftCheck({ status: 'failed' });
    } finally {
      setChecking(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) {
      return;
    }
    const rawErrors = validateDraft(kind, draft);
    const errors: Record<string, string> = {};
    for (const [field, key] of Object.entries(rawErrors)) {
      errors[field] = t(key);
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    const interval = draftIntervalSecs(draft);
    if (interval === null) {
      return;
    }
    const config = draftToConfig(kind, draft);
    const credentials = draftCredentials(draft);
    setPending(true);
    try {
      const saved =
        editing && source !== null
          ? await patchSource(token, source.id, {
              name: draft.name.trim(),
              config,
              ...(credentials !== undefined ? { credentials } : {}),
              syncIntervalSecs: interval,
            })
          : await createSource(token, {
              kind,
              name: draft.name.trim(),
              config,
              ...(credentials !== undefined ? { credentials } : {}),
              syncIntervalSecs: interval,
            });
      onOpenChange(false);
      showToast(
        'success',
        t(editing ? 'calendar.sources.updated_toast' : 'calendar.sources.created_toast'),
      );
      onSaved(saved);
    } catch {
      showToast('error', t('calendar.sources.failed_toast'));
    } finally {
      setPending(false);
    }
  }

  const fieldError = (field: string): string | undefined => fieldErrors[field];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] scroll-thin overflow-y-auto border-border bg-card p-0 sm:max-w-[560px]">
        <DialogHeader className="border-b border-border px-6 pb-4 pt-5">
          <DialogTitle className="text-[17px] font-semibold tracking-tight text-foreground">
            {editing && source !== null
              ? t('calendar.sources.form_edit', { name: source.name })
              : t('calendar.sources.form_title')}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-muted-foreground">
            {t('calendar.sources.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(event) => void handleSubmit(event)} noValidate>
          <div className="space-y-5 px-6 py-5">
            {/* kind: a create-time choice only - a saved source never changes protocol */}
            {editing ? (
              <p className="text-[12.5px] text-muted-foreground">
                <span className="mr-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                  {t(`calendar.sources.kind_${kind}`)}
                </span>
                {t('calendar.sources.check_edit_hint')}
              </p>
            ) : (
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  {t('calendar.sources.kind_label')}
                </Label>
                <div
                  className="mt-2 grid grid-cols-2 gap-2"
                  role="radiogroup"
                  aria-label={t('calendar.sources.kind_label')}
                >
                  {KIND_CHOICES.map((choice) => {
                    const active = kind === choice.kind;
                    return (
                      <button
                        key={choice.kind}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        data-testid={`source-kind-${choice.kind}`}
                        onClick={() => chooseKind(choice.kind)}
                        className={
                          active
                            ? 'rounded-lg border border-primary bg-primary/10 px-3 py-2 text-left text-[12.5px] font-semibold text-foreground'
                            : 'rounded-lg border border-border px-3 py-2 text-left text-[12.5px] font-medium text-muted-foreground transition-colors hover:border-muted-foreground/30 hover:text-foreground'
                        }
                      >
                        {t(choice.labelKey)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* name */}
            <div>
              <Label
                htmlFor="source-name"
                className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
              >
                {t('calendar.sources.name_label')}
              </Label>
              <Input
                id="source-name"
                data-testid="source-name"
                value={draft.name}
                onChange={(event) => update({ name: event.target.value })}
                placeholder={t('calendar.sources.name_placeholder')}
                aria-invalid={fieldError('name') !== undefined}
                className="mt-2 h-10 rounded-lg text-[13.5px]"
              />
              {fieldError('name') !== undefined ? (
                <p role="alert" className="mt-1 text-[11.5px] text-red-400">
                  {fieldError('name')}
                </p>
              ) : null}
            </div>

            {/* per-kind config fields */}
            {kind === 'caldav' ? (
              <>
                <div>
                  <Label
                    htmlFor="source-caldav-server"
                    className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                  >
                    {t('calendar.sources.fields.caldav_server_url')}
                  </Label>
                  <Input
                    id="source-caldav-server"
                    data-testid="source-caldav-server"
                    value={draft.serverUrl}
                    onChange={(event) => update({ serverUrl: event.target.value })}
                    placeholder={t('calendar.sources.fields.caldav_server_url_placeholder')}
                    aria-invalid={fieldError('config') !== undefined}
                    className="mt-2 h-10 rounded-lg font-mono text-[13px]"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="source-caldav-collection"
                    className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                  >
                    {t('calendar.sources.fields.caldav_collection')}
                  </Label>
                  <Input
                    id="source-caldav-collection"
                    data-testid="source-caldav-collection"
                    value={draft.collectionPath}
                    onChange={(event) => update({ collectionPath: event.target.value })}
                    placeholder={t('calendar.sources.fields.caldav_collection_placeholder')}
                    aria-invalid={fieldError('config') !== undefined}
                    className="mt-2 h-10 rounded-lg font-mono text-[13px]"
                  />
                </div>
              </>
            ) : null}

            {kind === 'ews' ? (
              <>
                <div>
                  <Label
                    htmlFor="source-ews-url"
                    className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                  >
                    {t('calendar.sources.fields.ews_url')}
                  </Label>
                  <Input
                    id="source-ews-url"
                    data-testid="source-ews-url"
                    value={draft.ewsUrl}
                    onChange={(event) => update({ ewsUrl: event.target.value })}
                    placeholder={t('calendar.sources.fields.ews_url_placeholder')}
                    aria-invalid={fieldError('config') !== undefined}
                    className="mt-2 h-10 rounded-lg font-mono text-[13px]"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="source-ews-mailbox"
                    className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                  >
                    {t('calendar.sources.fields.ews_mailbox')}
                  </Label>
                  <Input
                    id="source-ews-mailbox"
                    data-testid="source-ews-mailbox"
                    value={draft.mailbox}
                    onChange={(event) => update({ mailbox: event.target.value })}
                    placeholder={t('calendar.sources.fields.ews_mailbox_placeholder')}
                    aria-invalid={fieldError('config') !== undefined}
                    className="mt-2 h-10 rounded-lg text-[13.5px]"
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <Label
                      htmlFor="source-ews-allow-http"
                      className="text-[12px] font-medium text-foreground"
                    >
                      {t('calendar.sources.fields.ews_allow_http')}
                    </Label>
                    <Switch
                      id="source-ews-allow-http"
                      checked={draft.allowHttp}
                      onCheckedChange={(checked) => update({ allowHttp: checked })}
                      data-testid="source-ews-allow-http"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <Label
                      htmlFor="source-ews-allow-ntlm"
                      className="text-[12px] font-medium text-foreground"
                    >
                      {t('calendar.sources.fields.ews_allow_ntlm')}
                    </Label>
                    <Switch
                      id="source-ews-allow-ntlm"
                      checked={draft.allowNtlm}
                      onCheckedChange={(checked) => update({ allowNtlm: checked })}
                      data-testid="source-ews-allow-ntlm"
                    />
                  </div>
                </div>
              </>
            ) : null}

            {kind === 'ad' ? (
              <>
                <div className="grid grid-cols-[1fr_110px] gap-3">
                  <div>
                    <Label
                      htmlFor="source-ad-host"
                      className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                    >
                      {t('calendar.sources.fields.ad_host')}
                    </Label>
                    <Input
                      id="source-ad-host"
                      data-testid="source-ad-host"
                      value={draft.adHost}
                      onChange={(event) => update({ adHost: event.target.value })}
                      placeholder={t('calendar.sources.fields.ad_host_placeholder')}
                      aria-invalid={fieldError('config') !== undefined}
                      className="mt-2 h-10 rounded-lg text-[13.5px]"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="source-ad-port"
                      className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                    >
                      {t('calendar.sources.fields.ad_port')}
                    </Label>
                    <Input
                      id="source-ad-port"
                      data-testid="source-ad-port"
                      type="number"
                      min={1}
                      max={65535}
                      value={draft.adPort}
                      onChange={(event) => update({ adPort: event.target.value })}
                      aria-invalid={fieldError('config') !== undefined}
                      className="mt-2 h-10 rounded-lg tabular-nums text-[13.5px]"
                    />
                  </div>
                </div>
                <div>
                  <Label
                    htmlFor="source-ad-basedn"
                    className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                  >
                    {t('calendar.sources.fields.ad_base_dn')}
                  </Label>
                  <Input
                    id="source-ad-basedn"
                    data-testid="source-ad-basedn"
                    value={draft.adBaseDn}
                    onChange={(event) => update({ adBaseDn: event.target.value })}
                    placeholder={t('calendar.sources.fields.ad_base_dn_placeholder')}
                    aria-invalid={fieldError('config') !== undefined}
                    className="mt-2 h-10 rounded-lg font-mono text-[13px]"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="source-ad-filter"
                    className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                  >
                    {t('calendar.sources.fields.ad_filter')}
                  </Label>
                  <Input
                    id="source-ad-filter"
                    data-testid="source-ad-filter"
                    value={draft.adFilter}
                    onChange={(event) => update({ adFilter: event.target.value })}
                    placeholder={t('calendar.sources.fields.ad_filter_placeholder')}
                    className="mt-2 h-10 rounded-lg font-mono text-[13px]"
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <Label
                    htmlFor="source-ad-starttls"
                    className="text-[12px] font-medium text-foreground"
                  >
                    {t('calendar.sources.fields.ad_start_tls')}
                  </Label>
                  <Switch
                    id="source-ad-starttls"
                    checked={draft.adStartTls}
                    onCheckedChange={(checked) => update({ adStartTls: checked })}
                    data-testid="source-ad-starttls"
                  />
                </div>
              </>
            ) : null}

            {kind === 'ics' ? (
              <div>
                <Label
                  htmlFor="source-ics-url"
                  className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                >
                  {t('calendar.sources.fields.ics_url')}
                </Label>
                <Input
                  id="source-ics-url"
                  data-testid="source-ics-url"
                  value={draft.icsUrl}
                  onChange={(event) => update({ icsUrl: event.target.value })}
                  placeholder={t('calendar.sources.fields.ics_url_placeholder')}
                  aria-invalid={fieldError('config') !== undefined}
                  className="mt-2 h-10 rounded-lg font-mono text-[13px]"
                />
              </div>
            ) : null}
            {fieldError('config') !== undefined ? (
              <p role="alert" className="text-[11.5px] text-red-400">
                {fieldError('config')}
              </p>
            ) : null}

            {/* credentials: optional for every kind (Basic auth / sealed server-side) */}
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div>
                <Label
                  htmlFor="source-username"
                  className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                >
                  {t('calendar.sources.username_label')}
                </Label>
                <Input
                  id="source-username"
                  data-testid="source-username"
                  value={draft.username}
                  onChange={(event) => update({ username: event.target.value })}
                  autoComplete="off"
                  aria-invalid={fieldError('username') !== undefined}
                  className="mt-2 h-10 rounded-lg text-[13.5px]"
                />
              </div>
              <div>
                <Label
                  htmlFor="source-password"
                  className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                >
                  {t('calendar.sources.password_label')}
                </Label>
                <Input
                  id="source-password"
                  data-testid="source-password"
                  type="password"
                  value={draft.password}
                  onChange={(event) => update({ password: event.target.value })}
                  autoComplete="new-password"
                  aria-invalid={fieldError('username') !== undefined}
                  className="mt-2 h-10 rounded-lg text-[13.5px]"
                />
              </div>
              <p className="text-[11px] leading-snug text-muted-foreground">
                {t(editing ? 'calendar.sources.credentials_note_edit' : 'calendar.sources.credentials_note')}
              </p>
              {fieldError('username') !== undefined ? (
                <p role="alert" className="text-[11.5px] text-red-400">
                  {fieldError('username')}
                </p>
              ) : null}
            </div>

            {/* sync interval */}
            <div>
              <Label
                htmlFor="source-interval"
                className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
              >
                {t('calendar.sources.interval_label')}
              </Label>
              <select
                id="source-interval"
                data-testid="source-interval"
                value={draft.intervalChoice}
                onChange={(event) => update({ intervalChoice: event.target.value })}
                className="mt-2 h-10 w-full cursor-pointer appearance-none rounded-lg border border-border bg-card px-3 text-[13.5px] text-foreground outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:[color-scheme:dark]"
              >
                {intervalOptions(editing && source !== null ? source.syncIntervalSecs : null).map(
                  (secs) => (
                    <option key={secs} value={`${secs}`}>
                      {intervalLabel(t, secs)}
                    </option>
                  ),
                )}
              </select>
            </div>

            {/* draft connection probe: create mode only - the edit form's saved credentials
                are not here, so the row-level Check answers for the stored state instead */}
            {editing ? null : (
              <div className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[12px] font-medium text-foreground">
                    {t('calendar.sources.title')}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 shrink-0 gap-1.5 text-[12px]"
                    data-testid="source-form-check"
                    disabled={checking}
                    onClick={() => void handleCheck()}
                  >
                    {checking ? (
                      <Loader2 aria-hidden className="size-3.5 animate-spin" />
                    ) : null}
                    {checking
                      ? t('calendar.sources.checking')
                      : t('calendar.sources.check')}
                  </Button>
                </div>
                {draftCheck !== null ? (
                  <div className="mt-2" data-testid="source-form-check-result">
                    {draftCheck.status === 'checking' ? null : draftCheck.status === 'failed' ? (
                      <p className="flex items-start gap-1.5 text-[12.5px] text-red-400">
                        <XCircle aria-hidden className="mt-0.5 size-3.5 shrink-0" />
                        {t('calendar.sources.check_failed_generic')}
                      </p>
                    ) : (
                      <CheckResultView
                        check={draftCheck.check}
                        kind={draftCheck.kind}
                        onUseCollection={(path) => update({ collectionPath: path })}
                      />
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <DialogFooter className="border-t border-border px-6 py-4">
            <div className="flex w-full gap-2 sm:w-auto">
              <Button
                type="button"
                variant="outline"
                className="h-9 flex-1 border-border text-[13px] sm:flex-none"
                onClick={() => onOpenChange(false)}
              >
                {t('calendar.sources.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={pending}
                className="h-9 flex-1 gap-1.5 text-[13px] font-semibold sm:flex-none"
              >
                {pending ? <Loader2 aria-hidden className="size-3.5 animate-spin" /> : null}
                {editing
                  ? pending
                    ? t('calendar.sources.saving')
                    : t('calendar.sources.save')
                  : pending
                    ? t('calendar.sources.submitting')
                    : t('calendar.sources.submit')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export interface CheckResultViewProps {
  /** The probe answer to render. */
  readonly check: SourceCheck;
  /** The kind the probe ran for (a CalDAV success renders the discovered collections). */
  readonly kind: SourceKind;
  /** Present in the create form: clicking a collection fills the collectionPath field. */
  readonly onUseCollection?: ((path: string) => void) | undefined;
}

/** The inline answer of a connection probe: ok/fail line plus, for a successful CalDAV
 * probe, the discovered collections as clickable (or read-only) rows. */
export function CheckResultView({
  check,
  kind,
  onUseCollection,
}: CheckResultViewProps): JSX.Element {
  const { t } = useTranslation();
  if (!check.ok) {
    return (
      <div className="text-[12.5px] text-red-400">
        <p className="flex items-start gap-1.5 font-medium">
          <XCircle aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          {t('calendar.sources.check_failed_generic')}
        </p>
        <p className="mt-0.5">
          {check.code !== null ? t(`calendar.sources.check_code.${check.code}`) : null}
        </p>
        {check.message !== null ? (
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{check.message}</p>
        ) : null}
      </div>
    );
  }
  const collections = check.calendars;
  return (
    <div className="text-[12.5px] text-emerald-400">
      <p className="flex items-start gap-1.5 font-medium">
        <CheckCircle2 aria-hidden className="mt-0.5 size-3.5 shrink-0" />
        {t('calendar.sources.check_ok')}
      </p>
      {kind === 'caldav' && collections.length > 0 ? (
        <>
          <p className="mt-1 text-[11.5px] text-muted-foreground">
            {t('calendar.sources.check_ok_collections', { count: collections.length })}
          </p>
          <ul className="mt-1.5 space-y-1">
            {collections.map((calendar) => (
              <li
                key={calendar.path}
                className="flex items-center justify-between gap-2 rounded-md border border-border bg-card px-2 py-1.5"
              >
                <span className="min-w-0">
                  <span className="block truncate text-[12px] font-medium text-foreground">
                    {calendar.displayName.length > 0 ? calendar.displayName : calendar.path}
                  </span>
                  <span className="block truncate font-mono text-[10.5px] text-muted-foreground">
                    {calendar.path}
                  </span>
                </span>
                {onUseCollection !== undefined ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 shrink-0 px-2 text-[11px]"
                    data-testid={`source-use-collection-${calendar.path}`}
                    onClick={() => onUseCollection(calendar.path)}
                  >
                    {t('calendar.sources.use_collection')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
